import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { findIntersections } from '../../../analytics/numerical-methods';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { IntersectionParams } from './intersection.model';

/**
 * Creates intersection points between two functions.
 *
 * Uses numerical methods (bisection) to find where f(x) - g(x) = 0.
 * Points are reactive and update when either parent function changes.
 */
export class IntersectionCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'intersection';

  /** Maximum number of intersection points to find */
  private readonly maxIntersections = 10;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;

    // Get both parent elements from params (can be functions or derivatives)
    const params = def.params as IntersectionParams | undefined;
    if (!params?.of || !Array.isArray(params.of) || params.of.length !== 2) {
      throw new Error('Intersection feature requires params.of to be a tuple of two element IDs');
    }
    // Use getEvaluableParents to support both functions and derivatives
    const [fn1, fn2] = this.getEvaluableParents(params.of, context);
    const objects: GeometryElement[] = [];

    // Resolve styles using the feature style cascade (label is now part of style)
    const style = resolveFeatureStyle('point', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    // Create intersection points
    // We create a fixed number of points and hide the ones that don't have intersections
    for (let i = 0; i < this.maxIntersections; i++) {
      const result = elementFactory.point({
        coords: [
          () => {
            const bbox = board.getBoundingBox();
            const intersections = findIntersections(fn1.evaluate!, fn2.evaluate!, bbox[0], bbox[2], {
              maxResults: this.maxIntersections,
            });
            return i < intersections.length ? intersections[i] : NaN;
          },
          () => {
            const bbox = board.getBoundingBox();
            const intersections = findIntersections(fn1.evaluate!, fn2.evaluate!, bbox[0], bbox[2], {
              maxResults: this.maxIntersections,
            });
            return i < intersections.length ? fn1.evaluate!(intersections[i]) : NaN;
          },
        ],
        name: 'intersection',
        style: { ...style, visible },
      });
      objects.push(result.point);
      if (result.label) {
        objects.push(result.label);
      }
    }

    return {
      id,
      type: 'intersection',
      objects,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
